import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import ApiService from '../services/apiServices';
import { getFromLocalStorage, removeFromLocalStorage } from '../utils/storage';
import {
  emisionDelToken,
  EVENTO_SESION_RECHAZADA,
  EVENTO_SESION_RENOVADA,
  RENOVAR_DESPUES_DE_MS,
  tokenVigente,
  vencimientoDelToken,
} from '../utils/sesion';

const AuthContext = createContext();

/** Motivo de logout() cuando la sesión terminó sola, no por el usuario. */
export const SESION_EXPIRADA = 'expirada';

/**
 * Cuánto tiempo sin tocar el panel sigue contando como «en uso». Pasado esto
 * no se renueva la sesión y vence sola a la hora de la última petición.
 */
const USO_RECIENTE_MS = 5 * 60 * 1000;

/** Cada cuánto se mira si hace falta renovar por uso sin peticiones. */
const REVISION_DE_USO_MS = 60 * 1000;

const EVENTOS_DE_USO = ['click', 'keydown', 'scroll', 'touchstart', 'mousemove'];

const leerToken = () => getFromLocalStorage('user')?.jwt ?? null;

/**
 * Pide la renovación. La respuesta no importa: si corresponde, trae el token
 * nuevo en la cabecera y apiServices lo guarda.
 */
const pedirRenovacion = () =>
  new ApiService()
    .get(`${process.env.REACT_APP_API_URL}/panel/sesion`)
    .catch(() => {
      // Un 401 ya cierra la sesión por su cuenta; cualquier otro fallo se
      // reintenta en la siguiente revisión.
    });

export const AuthProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  // El token vigente. Cada vez que cambia se reprograma el cierre automático.
  const [token, setToken] = useState(null);
  // Para que el login explique por qué se volvió a pedir la clave.
  const [sesionExpirada, setSesionExpirada] = useState(false);

  const logout = useCallback(
    (motivo) => {
      removeFromLocalStorage('user');
      // Sin esto, al entrar de nuevo se verían un instante los datos en caché
      // de la sesión anterior.
      queryClient.clear();
      setToken(null);
      setSesionExpirada(motivo === SESION_EXPIRADA);
      setIsAuthenticated(false);
    },
    [queryClient]
  );

  const login = useCallback(() => {
    const jwt = leerToken();

    if (tokenVigente(jwt)) {
      setToken(jwt);
      setSesionExpirada(false);
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
    }
  }, []);

  // Al abrir el panel: una sesión guardada puede haber vencido mientras
  // estaba cerrado.
  useEffect(() => {
    const jwt = leerToken();

    if (!jwt) {
      setIsAuthenticated(false);
    } else if (tokenVigente(jwt)) {
      setToken(jwt);
      setIsAuthenticated(true);
    } else {
      logout(SESION_EXPIRADA);
    }
  }, [logout]);

  // Mientras hay sesión, se cierra sola al vencer el token.
  useEffect(() => {
    if (!token) return undefined;

    const revisar = () => {
      const actual = leerToken();

      // Otra pestaña cerró la sesión.
      if (!actual) {
        logout();
        return;
      }

      // Otra pestaña inició sesión de nuevo: el token es otro y el cierre
      // se reprograma con su vencimiento.
      if (actual !== token) {
        setToken(actual);
        return;
      }

      if (!tokenVigente(actual)) logout(SESION_EXPIRADA);
    };

    // El temporizador cubre el caso normal. No basta solo: con el equipo
    // suspendido o la pestaña en segundo plano el navegador lo congela, y por
    // eso también se revisa al volver a la pestaña.
    const vence = vencimientoDelToken(token);
    const temporizador =
      vence === null
        ? null
        : setTimeout(revisar, Math.max(vence - Date.now(), 0) + 1000);

    const alVolver = () => {
      if (document.visibilityState === 'visible') revisar();
    };

    // El API rechazó el token (vencido antes de tiempo, reloj del equipo
    // adelantado o atrasado, secreto cambiado...).
    const alRechazo = () => logout(SESION_EXPIRADA);

    // El API entregó un token renovado (apiServices ya lo guardó): cambiar el
    // token reprograma este efecto con el nuevo vencimiento.
    const alRenovar = () => setToken(leerToken());

    const alCambiarOtraPestana = (evento) => {
      if (evento.key === 'user' || evento.key === null) revisar();
    };

    document.addEventListener('visibilitychange', alVolver);
    window.addEventListener('focus', revisar);
    window.addEventListener(EVENTO_SESION_RECHAZADA, alRechazo);
    window.addEventListener(EVENTO_SESION_RENOVADA, alRenovar);
    window.addEventListener('storage', alCambiarOtraPestana);

    return () => {
      if (temporizador) clearTimeout(temporizador);
      document.removeEventListener('visibilitychange', alVolver);
      window.removeEventListener('focus', revisar);
      window.removeEventListener(EVENTO_SESION_RECHAZADA, alRechazo);
      window.removeEventListener(EVENTO_SESION_RENOVADA, alRenovar);
      window.removeEventListener('storage', alCambiarOtraPestana);
    };
  }, [token, logout]);

  // Uso del panel sin peticiones al API (llenar un formulario, leer una
  // ficha): cada petición ya renueva la sesión por su cuenta, pero si el
  // usuario trabaja un rato sin que la pantalla consulte nada, se pide la
  // renovación para que no lo saque a mitad de lo que está haciendo.
  const ultimoUso = useRef(Date.now());

  useEffect(() => {
    if (!token) return undefined;

    const marcarUso = () => {
      ultimoUso.current = Date.now();
    };

    const revisarUso = () => {
      const enUso = Date.now() - ultimoUso.current < USO_RECIENTE_MS;
      const emitido = emisionDelToken(token);
      const renovable =
        emitido !== null && Date.now() - emitido >= RENOVAR_DESPUES_DE_MS;

      if (enUso && renovable) pedirRenovacion();
    };

    EVENTOS_DE_USO.forEach((evento) =>
      window.addEventListener(evento, marcarUso, { passive: true, capture: true })
    );
    const intervalo = setInterval(revisarUso, REVISION_DE_USO_MS);

    return () => {
      EVENTOS_DE_USO.forEach((evento) =>
        window.removeEventListener(evento, marcarUso, { capture: true })
      );
      clearInterval(intervalo);
    };
  }, [token]);

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, login, logout, sesionExpirada }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
